<template>
  <!-- 新增/编辑用户 -->
  <custom-form
    ref="refForm"
    :formData="formData"
    :formRules="formRules"
    @changeFun="changeFun"
    @submitForm="submitForm"
  />
</template>

<script setup>
import { reactive, nextTick, ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { rolesOptions } from '@/dataJson/users'
import { verifyField } from '@/api/users/tenant'
import { getMobileAreas } from '@/api/app'
import checkRules from '@/components/CustomCheck'

const { t } = useI18n()
const refForm = ref(null)
// 获取手机号国家（区）码
const mobileAreasOptions = ref([])
const roleOptions = rolesOptions // 用户角色

const props = defineProps({
  scene: {
    default: '',
    type: String
  },
  // 类型，是新增还是编辑
  editType: {
    default: '',
    type: String
  },
  rowData: {
    default: () => {},
    type: Object
  }
})
// 表单数据
const formData = reactive({
  username: {
    type: 'text',
    label: t('user.userName'),
    value: '',
    prop: 'username',
    placeholder: ''
  },
  password: {
    type: 'password',
    label: t('user.password'),
    value: '',
    prop: 'password',
    placeholder: ''
  },
  roles: {
    type: 'select',
    label: t('user.role'),
    value: '',
    prop: 'roles',
    placeholder: '',
    options: roleOptions
  },
  nickname: {
    type: 'text',
    label: t('user.nickName'),
    value: '',
    prop: 'nickname',
    placeholder: ''
  },
  email: {
    type: 'text',
    label: t('user.email'),
    value: '',
    prop: 'email',
    msg: '',
    showError: false,
    placeholder: ''
  },
  notification_language: {
    type: 'select',
    label: t('user.notification_language'),
    value: 'zh',
    prop: 'notification_language',
    required: true,
    options: [
      {
        id: 'zh',
        name: '简体中文'
      },
      {
        id: 'en',
        name: 'English'
      }
    ],
    tip: t('user.notification_language_tip')
  },
  area: {
    type: 'select',
    label: t('user.area'),
    value: null,
    prop: 'area',
    placeholder: '',
    options: mobileAreasOptions.value
  },
  mobile: {
    type: 'text',
    label: t('user.mobile'),
    value: '',
    prop: 'mobile',
    msg: '',
    showError: false,
    placeholder: ''
  },
  user_status: {
    type: 'select',
    label: t('table.status'),
    value: 'enabled',
    prop: 'user_status',
    placeholder: '',
    options: [
      {
        id: 'enabled',
        name: t('table.enable')
      },
      {
        id: 'disabled',
        name: t('table.disable')
      }
    ]
  }
})

// 表单校验规则
const { emptCheck } = checkRules(t)
// 表单校验函数
const checkFun = async(rule, modleVal, callback) => {
  if (!modleVal.value) {
    callback(new Error(t('form.emptyErrMsg')))
  } else {
    if (['addUser', 'add'].includes(props.editType) || props.editType === 'editUser' && props.rowData[rule.field] !== modleVal.value) {
      const scene = props.scene === 'null' ? undefined : props.scene
      const res = await verifyField({ [rule.field]: modleVal.value, scene: scene })
      if (res.data[rule.field].status === 'ok') {
        callback()
      } else {
        callback(new Error(res.data[rule.field].msg))
      }
    }
  }
}
// 密码
const checkPassword = (rule, modleVal, callback) => {
  // 8-20个字符且必须包含字母和数字，支持特殊字符
  const patter = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d.!\"@#$%&'*():;\\+/=?^_`{|}\[\]~><-]{8,20}$/
  if (!!!modleVal.value) {
    callback(new Error(t('form.emptyErrMsg')))
  } else if (!!modleVal.value && !patter.test(modleVal.value)) {
    callback(new Error(t('login.newRulesPwdMsg')))
  } else {
    callback()
  }
}
// 邮箱
const emailPatter = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const checkMail = async(rule, modleVal, callback) => {
  if (!modleVal.value || !emailPatter.test(modleVal.value)) {
    callback(new Error(t('form.emailError')))
  } else {
    if (['addUser', 'add'].includes(props.editType) || props.editType === 'editUser' && props.rowData[rule.field] !== modleVal.value) {
      const scene = props.scene === 'null' ? undefined : props.scene
      const res = await verifyField({ email: modleVal.value, scene: scene })
      if (res.data['email'].status === 'ok') {
        callback()
      } else {
        callback(new Error(res.data['email'].msg))
      }
    }
  }
}

// 手机号-必须为5-20位数字
const checkMobile = (rule, modleVal, callback) => {
  if (!modleVal.value) {
    callback()
    return
  }

  // 只有中国手机号单独校验
  if (modleVal.value && formData.area.value === 'zh') {
    const chinaMobilePatter = /^1[3-9]\d{9}$/
    if (!chinaMobilePatter.test(modleVal.value)) {
      callback(new Error(t('login.mobileMsg')))
    } else {
      callback()
    }
    return
  }

  if (!/^\d{1,}$/.test(modleVal.value)) {
    callback(new Error(t('login.numberMsg')))
  } else if (!/^\d{5,20}$/.test(modleVal.value)) {
    callback(new Error(t('login.mobileMsg')))
  } else {
    callback()
  }
}

// 表单校验规则
const formRules = computed(() => {
  const rules = {
    // 其他字段
    roles: emptCheck,
    notification_language: emptCheck,
    username: [{ required: true, trigger: ['blur'], validator: checkFun }],
    password: [{ required: true, trigger: ['blur', 'change'], validator: checkPassword }],
    email: [{ required: true, trigger: ['blur'], validator: checkMail }],
    mobile: [{ required: false, trigger: ['blur', 'change'], validator: checkMobile }]
  }
  return rules
})
const changeFun = () => {}
const submitForm = (val) => {
  return new Promise(resolve => {
    val.validate(valid => {
      resolve(!!valid)
    })
  })
}
// 提交
const submit = async() => {
  await nextTick()
  return new Promise(resolve => {
    refForm.value.$refs.refForm.validate(valid => {
      resolve(!!valid)
    })
  })
}

// 获取区域列表
const getMobileList = async() => {
  const res = await getMobileAreas()
  formData.area.options = res.data.areas
}

watch(() => props.editType, (val) => {
  if (!val) return
  if (val === 'editUser') {
    if (formData.password) delete formData.password
  }
  if (val !== 'resetPassword') {
    getMobileList()
  }
}, { deep: true, immediate: true })

// 监听区域变化
watch(() => formData && formData.area && formData.area.value, (newVal, oldVal) => {
  // area 变化时，手动校验 mobile 字段
  nextTick(() => {
    if (refForm.value && refForm.value.$refs && refForm.value.$refs.refForm) {
      refForm.value.$refs.refForm.validateField('mobile')
    }
  })
})

defineExpose({
  submit,
  formData
})
</script>